import * as React from "react"
import { Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import H4 from "../../../atoms/typography/H4"
import { HorizontalSpacer, Spacer } from "../../../atoms/spacers"
import H3 from "../../../atoms/typography/H3"
import Body from "../../../atoms/typography/Body"
import { ArrowDropUpIcon } from "../../../atoms/icons/ArrowDropUpIcon"
import { ArrowDropDownIcon } from "../../../atoms/icons/ArrowDropDownIcon"
import { getFormattedPrice } from "../../../../utils/cart-helpers"
//@ts-ignore
import IntelsatLogo from "../../../atoms/icons/images/intelsatlogo.svg"
import { Product } from "../../../interfaces/SaleorTypes"
import { MediaQuery } from "../../../../utils/design-helpers"

interface TerminalProps {
  name: string
  amount: number
  modemName: string
  warranty: number
  total: number
  currency?: string
  open: boolean
  onClick: () => void
  disabled?: boolean
}

const NumberContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.greyDark};
`

const TitleSection = styled.div`
  display: flex;
  align-items: center;
`

const ClickableRow = styled.div<{ disabled?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${MediaQuery.XS} {
    flex-direction: column;
    align-items: flex-start;
  }

  :hover {
    cursor: ${(props) => (props.disabled ? undefined : "pointer")};
  }

  filter: ${(props) => (props.disabled ? "opacity(0.3)" : undefined)};
`

const CircleContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid #c3c3c3;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  overflow: clip;
`

const AccessoryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const PaddedRow = styled.div`
  padding: 16px;

  ${MediaQuery.XS} {
    padding: 0;
  }
`

export const CustomizeTerminalFinalBar: React.FC<TerminalProps> = ({
  name,
  amount,
  modemName,
  warranty,
  total,
  currency,
  open,
  onClick,
  disabled,
}) => {
  const warrantyTitle =
    warranty === 0 ? "One year warranty" : warranty === 1 ? "Two year warranty" : "Three year warranty"

  return (
    <PaddedRow>
      <ClickableRow onClick={disabled ? undefined : onClick} disabled={disabled}>
        <TitleSection>
          <NumberContainer>
            <H4 color="white" textAlign="center">
              1
            </H4>
          </NumberContainer>
          <Spacer spacing={1} />

          <H3>Terminals</H3>
        </TitleSection>
        <Visible xs>
          <HorizontalSpacer spacing={1} />
        </Visible>
        <div>
          <H3>
            {amount}x {name}
          </H3>
          <Visible xs>
            <HorizontalSpacer spacing={1} />
          </Visible>
          <Body>{modemName}</Body>
        </div>
        <Visible xs>
          <HorizontalSpacer spacing={1} />
        </Visible>
        <H3>{warrantyTitle}</H3>
        <Visible xs>
          <HorizontalSpacer spacing={1} />
        </Visible>
        <H3>Price: {getFormattedPrice(total, currency)}</H3>

        <Hidden xs>{open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}</Hidden>
      </ClickableRow>
    </PaddedRow>
  )
}

interface DataPlanProps {
  dataPlanName?: string
  dataPlanSize?: string
  total: number
  currency?: string
  open: boolean
  onClick: () => void
  disabled?: boolean
}

export const CustomizeDataPlanFinalBar: React.FC<DataPlanProps> = ({
  dataPlanName,
  dataPlanSize,
  total,
  currency,
  open,
  onClick,
  disabled,
}) => {
  return (
    <PaddedRow>
      <ClickableRow onClick={disabled ? undefined : onClick} disabled={disabled}>
        <TitleSection>
          <NumberContainer>
            <H4 color="white" textAlign="center">
              2
            </H4>
          </NumberContainer>
          <Spacer spacing={1} />
          <Visible xs>
            <HorizontalSpacer spacing={1} />
          </Visible>
          <H3>Data plan</H3>
        </TitleSection>
        <Visible xs>
          <HorizontalSpacer spacing={1} />
        </Visible>
        <IntelsatLogo />
        <Visible xs>
          <HorizontalSpacer spacing={1} />
        </Visible>
        <div>
          <H3>{dataPlanName}</H3>
          <Visible xs>
            <HorizontalSpacer spacing={1} />
          </Visible>
          <Body>{dataPlanSize} - 1 year validity</Body>
        </div>
        <Visible xs>
          <HorizontalSpacer spacing={1} />
        </Visible>
        <H3>Price: {getFormattedPrice(total, currency)}</H3>

        <Hidden xs>{open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}</Hidden>
      </ClickableRow>
    </PaddedRow>
  )
}

interface AccessoryProps {
  open: boolean
  onClick: () => void
  items: Array<{ product: Product; amount: number }>
  total: number
  currency?: string
  disabled?: boolean
}

export const CustomizeAccessoriesFinalBar: React.FC<AccessoryProps> = ({
  open,
  onClick,
  items,
  total,
  currency,
  disabled,
}) => {
  return (
    <PaddedRow>
      <ClickableRow onClick={disabled ? undefined : onClick} disabled={disabled}>
        <TitleSection>
          <NumberContainer>
            <H4 color="white" textAlign="center">
              3
            </H4>
          </NumberContainer>
          <Spacer spacing={1} />
          <H3>Accessories</H3>
        </TitleSection>
        <Visible xs>
          <HorizontalSpacer spacing={1} />
        </Visible>
        <Body>Satcube starter pack (included)</Body>
        {items.map((x) => (
          <div key={x.product.id}>
            <TitleSection>
              <CircleContainer>
                <AccessoryImage src={x.product.media[0]?.url} />
              </CircleContainer>
              <Spacer spacing={0.5} />
              <H3>x {x.amount}</H3>
            </TitleSection>
          </div>
        ))}
        <Visible xs>
          <HorizontalSpacer spacing={1} />
        </Visible>
        <H3>Price: {getFormattedPrice(total, currency)}</H3>
        <Hidden xs>{open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}</Hidden>
      </ClickableRow>
    </PaddedRow>
  )
}
